import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { Divider, Grid, IconButton, Skeleton, useMediaQuery, useTheme } from '@mui/material';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import moment from 'moment';
import RefreshIcon from '@mui/icons-material/Refresh';
import { extendedDashboardFetcher } from '../../orders/dashboards/ExtendedDashboard';
import { PeriodStats } from './PeriodStat';
import { useAuth } from '../../session/InternalAuthProvider';
import { AccountData } from '../../api';
import { getDateFormat, getTimeFormat } from '../../common/getDateAndTimeFormat';

export const CompactStats: React.FC = () => {
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const { shortDateFormat } = getDateFormat(account);
  const { t } = useTranslation();
  const [lastFetch, setLastFetch] = useState<moment.Moment>(moment());
  const [isRefetching, setIsRefetching] = useState(false);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    data,
    isLoading: swrLoading,
    mutate: refetch,
  } = useSWR({ opId: 'getExtendedDashboard' }, extendedDashboardFetcher, {
    refreshInterval: 300000,
  });
  const handleRefresh = async () => {
    setIsRefetching(true);
    await refetch();
    setLastFetch(moment());
    setIsRefetching(false);
  };
  const isLoading = swrLoading || isRefetching;

  return (
    <Grid
      container
      alignItems="center"
      gap={2}
      mt={isSmallScreen ? -0.5 : undefined}
      sx={{
        p: 1,
        backgroundColor: 'background.paper',
        boxShadow: 1,
      }}>
      {isLoading ? (
        <Skeleton width={isSmallScreen ? 80 : 250} height={isSmallScreen ? 28 : 40} />
      ) : (
        <PeriodStats data={data?.today} label={t('dashboard.today')} />
      )}
      <Divider orientation="vertical" flexItem />
      {isLoading ? (
        <Skeleton width={isSmallScreen ? 80 : 250} height={isSmallScreen ? 28 : 40} />
      ) : (
        <PeriodStats data={data?.yesterday} label={t('dashboard.yesterday')} />
      )}
      {!isSmallScreen && <Divider orientation="vertical" flexItem />}
      {isLoading ? (
        <Skeleton width={isSmallScreen ? 250 : 250} height={40} />
      ) : (
        <Grid
          container
          mt={isSmallScreen ? -3 : undefined}
          mb={isSmallScreen ? -2 : undefined}
          xs={isSmallScreen ? 12 : 2}
          alignItems={'center'}>
          <Grid>
            <Typography color="text.secondary">{t('dashboard.lastUpdate')}: </Typography>
          </Grid>
          <Grid mx={isSmallScreen ? 1 : 0}>
            <Typography color="text.secondary">{` ${lastFetch.format(getTimeFormat(account))}`}</Typography>
          </Grid>
          <IconButton size="small" onClick={handleRefresh} disabled={isLoading} sx={{ ml: 0.5 }}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Grid>
      )}
    </Grid>
  );
};
